import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Button, Group, Modal, Text } from '@mantine/core';

/**
 * The one thing read first makes worse, and the answer to it.
 *
 * Opening a screen in edit mode meant nobody had much to lose by wandering off it: the values on
 * screen were the saved ones. Editing is a deliberate act now, so anything typed is work somebody
 * meant to do, and walking into another section of settings with it half finished has to be worth
 * a question. The rule is that the question is only ever asked when something actually changed:
 * a guard that fires on an untouched form is a guard people learn to dismiss without reading, and
 * then it does not guard anything.
 *
 * It is done by watching clicks rather than with the router's own blocker, which needs a data
 * router this app does not have (App mounts BrowserRouter and a plain route tree). A capture
 * listener on the document sees a link click before React does, so nothing has to know that a
 * navigation was about to happen. Leaving really leaves: the held changes are dropped and the
 * anchor is clicked again, which is the same navigation the person asked for the first time.
 *
 * The Back button is the other way out, and the most natural one: a half filled page is usually
 * left by pressing Back rather than by clicking something. It cannot be caught the same way,
 * because by the time `popstate` arrives the browser has already moved, and a guard that let the
 * router see that move would be worthless: the screen would unmount and take the changes with it,
 * so there would be nothing left to stay on. So the move is made harmless before it happens. While
 * anything is held, a copy of the current entry sits on top of the stack, and Back lands on its
 * twin: same address, same rendered screen, nothing unmounted, everything typed still there. All
 * the prompt has to do then is put the copy back, which is a `pushState` and not a navigation.
 *
 * Leaving from there is two entries back, past the copy and past the entry it copies, which is
 * the step the person asked for. Pressing Back twice quickly is safe because each press lands on
 * the twin and is answered with another copy, so the second press has the same effect as the
 * first rather than falling off the stack.
 *
 * A copy is never taken away again once it has been made, because removing it means another
 * traversal, and a traversal that crosses a save or a keystroke can leave somebody one press away
 * from an unguarded exit. A copy nobody needs any more is skipped instead, the moment it is landed
 * on, which costs one silent step to the identical entry underneath. The visible price is that
 * pressing Forward onto a spent copy stays where it is; Back, which is what people press, is
 * always right.
 */

interface UnsavedChanges {
  /** Say whether one open editor is holding changes nobody has saved. */
  readonly hold: (id: string, unsaved: boolean) => void;
  /** Forget an editor, when it closes or its screen goes away. */
  readonly release: (id: string) => void;
}

/**
 * A working guard that holds nothing, for anything rendered without the provider.
 *
 * An editor mounted on its own should still work; it just cannot warn anybody. Throwing here would
 * turn a missing provider into a blank screen, which is a worse trade than a missing warning.
 */
const NOTHING_HELD: UnsavedChanges = { hold: () => undefined, release: () => undefined };

const UnsavedChangesContext = createContext<UnsavedChanges>(NOTHING_HELD);

export function useUnsavedChanges(): UnsavedChanges {
  return useContext(UnsavedChangesContext);
}

/**
 * The mark on the entries this guard pushes, carried in `history.state` beside what the router
 * keeps there. It has to be on the entry rather than in a ref, because a ref cannot say which side
 * of a copy a `popstate` has landed on.
 */
const GUARD = '__quizeeGuard';

/** Whether the entry showing now is one of ours. */
function onGuardEntry(): boolean {
  const state: unknown = window.history.state;
  return typeof state === 'object' && state !== null && GUARD in state;
}

/**
 * Put a copy of the current entry on top of the stack.
 *
 * The address does not change and the router is not told, so nothing re-renders and no screen
 * unmounts. Whatever the router keeps in `history.state` is carried across, so the copy is the
 * same entry to it as the one underneath.
 */
function pushGuardEntry(): void {
  const state: unknown = window.history.state;
  const carried = typeof state === 'object' && state !== null ? state : {};
  window.history.pushState({ ...carried, [GUARD]: true }, '', window.location.href);
}

/** What somebody is being asked about: a link they clicked, or the Back button they pressed. */
type Leaving =
  { readonly kind: 'link'; readonly anchor: HTMLAnchorElement } | { readonly kind: 'back' };

/** Whether this click is one the browser would treat as following the link itself. */
function isPlainClick(event: MouseEvent): boolean {
  return (
    !event.defaultPrevented &&
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey
  );
}

export function UnsavedChangesProvider({ children }: { children: ReactNode }) {
  // A ref rather than state: the listener below reads it at the moment of a click, and holding
  // changes must not re-render every screen that is not editing anything.
  const held = useRef<Set<string>>(new Set());
  // Whether the copy on top of the stack is still the one a Back press would land off. It stops
  // being that the moment it is popped, and it is never true unless we put a copy there.
  const guarded = useRef(false);
  const [pending, setPending] = useState<Leaving | null>(null);

  /** Make sure a copy of this entry exists while there is anything to lose. */
  const armBack = useCallback(() => {
    if (held.current.size === 0) return;
    // Already sitting on one, from a prompt that was answered with Stay or from an earlier edit
    // on this screen. A second copy would only mean a second press to get past.
    if (onGuardEntry()) {
      guarded.current = true;
      return;
    }
    pushGuardEntry();
    guarded.current = true;
  }, []);

  const hold = useCallback(
    (id: string, unsaved: boolean) => {
      if (unsaved) held.current.add(id);
      else held.current.delete(id);
      armBack();
    },
    [armBack],
  );

  const release = useCallback((id: string) => {
    held.current.delete(id);
  }, []);

  useEffect(() => {
    function intercept(event: MouseEvent) {
      if (held.current.size === 0 || !isPlainClick(event)) return;
      const target = event.target instanceof Element ? event.target : null;
      const anchor = target?.closest('a[href]');
      if (!(anchor instanceof HTMLAnchorElement)) return;
      // A link opening somewhere else leaves this screen where it is, so it is not a way out.
      if (anchor.target !== '' && anchor.target !== '_self') return;
      if (anchor.dataset['unguarded'] === 'true') return;

      event.preventDefault();
      event.stopPropagation();
      setPending({ kind: 'link', anchor });
    }

    document.addEventListener('click', intercept, true);
    return () => document.removeEventListener('click', intercept, true);
  }, []);

  /**
   * The Back button, caught on the copy this guard keeps on top of the stack.
   *
   * Everything here runs while the browser is already on another entry, so it is written to be
   * true whichever entry that turns out to be rather than to assume the common one.
   */
  useEffect(() => {
    function popped() {
      if (onGuardEntry()) {
        // A copy from an earlier edit, reached by going back to this screen from somewhere else.
        // It is the same entry as the one below it, so stepping over it is invisible and saves
        // the next Back press doing nothing at all.
        guarded.current = false;
        window.history.back();
        return;
      }

      if (!guarded.current) return;
      // Our copy has just been popped, so it is no longer between anybody and the way out.
      guarded.current = false;

      if (held.current.size === 0) {
        // Nothing to ask about, and the entry now showing is identical to the one just left, so
        // the press would otherwise have looked like nothing happening. Finish the step.
        window.history.back();
        return;
      }

      // Same address, same screen, same unsaved values: only the position in the stack moved, and
      // this puts that back before anything is asked.
      pushGuardEntry();
      guarded.current = true;
      setPending({ kind: 'back' });
    }

    window.addEventListener('popstate', popped);
    return () => window.removeEventListener('popstate', popped);
  }, []);

  /**
   * Closing the tab is the one way out this cannot ask about itself.
   *
   * The browser shows its own wording and ignores ours, so there is nothing to say here beyond
   * that there is something to lose.
   */
  useEffect(() => {
    function warn(event: BeforeUnloadEvent) {
      if (held.current.size === 0) return;
      event.preventDefault();
    }

    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, []);

  function leave() {
    const going = pending;
    setPending(null);
    if (going === null) return;
    // Dropped first, so whatever happens below is not caught by this same guard a second time.
    held.current.clear();

    if (going.kind === 'link') {
      going.anchor.click();
      return;
    }

    // Two back: over the copy this guard put on top, and then the step the person actually
    // pressed Back for. A tab whose first entry is this screen has nothing that far back, and
    // there the browser does nothing, which is the same nothing Back would have done without a
    // guard in the way.
    guarded.current = false;
    window.history.go(-2);
  }

  return (
    <UnsavedChangesContext.Provider value={{ hold, release }}>
      {children}
      <Modal
        opened={pending !== null}
        onClose={() => setPending(null)}
        title="You have changes you have not saved"
        radius="lg"
        centered
      >
        <Text size="sm">
          Something on this screen has been changed and not saved yet. Going somewhere else now
          loses it, and there is no way back to it afterwards.
        </Text>
        <Group mt="lg">
          <Button color="red" onClick={leave} autoFocus>
            Leave and lose the changes
          </Button>
          <Button variant="default" onClick={() => setPending(null)}>
            Stay on this screen
          </Button>
        </Group>
      </Modal>
    </UnsavedChangesContext.Provider>
  );
}
